/**
 * Battery (akü), lift (direk) and eco-driving reports (ADR 0034): the shapes the console receives from
 * report_battery / report_lift / report_eco (SECURITY INVOKER, RLS applies; files through export_report).
 * Fictional sample data in demo mode. ADR 0003: counting and reporting only.
 */
import type { BatterySource, EcoEventType } from "./analog.js";
import type { DeviceFamily } from "./imei.js";
import type { DrivingGroup, DrivingRowKind } from "./driving.js";

export const TELEMATICS_REPORT_LIMITS = Object.freeze({
  /** Longest range (local days, inclusive). SQL: report_check(…, 31). */
  maxDays: 31,
  /** Vehicles × days (same bound as the driving report). SQL: fts:report_too_large. */
  maxVehicleDays: 400,
  /** Battery series bucket (s). */
  seriesBucketS: 15 * 60,
  /** At most this many eco events in the event list / map. */
  maxEcoEvents: 5000,
});

/* ---------------- battery ---------------- */

export type BatteryPoint = { at: string; avgV: number; minV: number; maxV: number };
export type BatterySession = {
  start: string;
  end: string;
  durS: number;
  startV: number;
  endV: number;
  peakV: number;
  opportunity: boolean;
  open: boolean;
};
export type BatteryVehicle = {
  vehicleId: string;
  fleetNo: string;
  family: DeviceFamily | null;
  /** Where the voltage comes from; "none" = no battery source (nothing is shown as battery voltage). */
  source: BatterySource;
  /** Readings in the range (0 with a source = no data). */
  readings: number;
  minV: number | null;
  maxV: number | null;
  avgV: number | null;
  /** The vehicle's low-battery threshold (null = alert off). */
  lowV: number | null;
  /** Readings below the threshold. */
  lowReadings: number;
  /** battery_low alarms in the range. */
  lowAlarms: number;
  /** The vehicle's charge detection rise of the fallback rule (V). */
  riseV: number;
  /** Charge level used by the estimate (V); null = unknown (fallback rule). */
  levelV: number | null;
  series: BatteryPoint[];
  sessions: BatterySession[];
};
export type BatteryTotals = { vehicles: number; measured: number; charges: number; chargeS: number; opportunity: number; lowAlarms: number };
export type BatteryResult = {
  tz: string;
  bucketS: number;
  /** Detection rules of the charge ESTIMATE (shown under the report). */
  rules: { minS: number; opportunityMaxS: number; holdS: number; settleS: number; medianN: number; levelVPerCell: number; endVPerCell: number };
  vehicles: BatteryVehicle[];
  totals: BatteryTotals;
};

/* ---------------- lift ---------------- */

export type LiftMetrics = { lifts: number; liftS: number; whileDriving: number; whileDrivingS: number };
export type LiftRow = LiftMetrics & {
  kind: DrivingRowKind;
  id: string | null;
  name: string | null;
  cards: number;
  vehicles: number;
  /** KVKK: a single hidden operator is counted only (metrics zero and not shown). */
  suppressed: boolean;
};
export type LiftResult = {
  tz: string;
  group: DrivingGroup;
  rows: LiftRow[];
  totals: LiftMetrics & { vehicles: number; operators: number };
  /** Vehicles of the scope without a lift source (no lift data can be shown for them). */
  unconfigured: { vehicleId: string; fleetNo: string }[];
};

/* ---------------- eco driving ---------------- */

export type EcoCounts = { accel: number; brake: number; corner: number; total: number; maxG: number | null };
export type EcoRow = EcoCounts & {
  kind: DrivingRowKind;
  id: string | null;
  name: string | null;
  cards: number;
  vehicles: number;
  suppressed: boolean;
};
export type EcoEventRow = {
  at: string;
  vehicleId: string;
  fleetNo: string;
  type: EcoEventType;
  g: number | null;
  /** Visible operator's name; null = no card, unknown card or an operator the caller may not see. */
  operatorName: string | null;
  lat: number | null;
  lon: number | null;
};
export type EcoResult = {
  tz: string;
  group: DrivingGroup;
  rows: EcoRow[];
  totals: EcoCounts & { vehicles: number; operators: number };
  events: EcoEventRow[];
  /** More events than the list holds. */
  truncated: boolean;
  /** Events of operators the caller may not see: counted in the rows, never listed (KVKK). */
  hiddenEvents: number;
  /** Scope vehicles and whether their device reports eco events (accelerometer unit). */
  sources: { vehicleId: string; fleetNo: string; family: DeviceFamily | null; eco: boolean }[];
};

/** Totals over the vehicles of a battery report (computed the same way for the server and the demo results). */
export function batteryTotals(vs: readonly BatteryVehicle[]): BatteryTotals {
  let charges = 0;
  let chargeS = 0;
  let opportunity = 0;
  let lowAlarms = 0;
  for (const v of vs) {
    charges += v.sessions.length;
    for (const s of v.sessions) {
      chargeS += s.durS;
      if (s.opportunity) opportunity++;
    }
    lowAlarms += v.lowAlarms;
  }
  return { vehicles: vs.length, measured: vs.filter((v) => v.source !== "none" && v.readings > 0).length, charges, chargeS: Math.round(chargeS), opportunity, lowAlarms };
}
